import React, { useEffect, useState } from 'react';
import instance from '../../service/axiosInstane';
import MuiTable from '../../components/MuiTable/MuiTable';
import PageHeading from '../../components/PageHeading/PageHeading';
import SearchInput from '../../components/SearchInput/SearchInput';
import Button from '../../components/Button/Button';
import {
  updateFlashBar,
  updateIsLoading,
  update_breadcrumb,
} from '../../redux/action';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import InputForm from '../../components/InputForm/InputForm';
import Input from '../../components/Input/Input';
import { flashBarTheme } from '../../config/flashBar';
import { errorMessage } from '../../config/errorsMsg';
import { SortOrderType } from '../../config/enum';
import { errorMessageAutoCapitalize, totalPages } from '../../config/helpers';
import { Column } from '../../components/MuiTable/ViewData';
import { successMessage } from '../../config/keyword';

const columns: Column[] = [
  {
    id: 'sequenceNumber',
    label: 'Site Number',
    minWidth: 120,
    align: 'center',
  },
  {
    id: 'siteUrl',
    label: 'Site Url',
    minWidth: 70,
    align: 'left',
  },
  {
    id: 'host',
    label: 'Host',
    minWidth: 70,
    align: 'left',
  },
  {
    id: 'action',
    label: 'Action',
    minWidth: 0,
    align: 'right',
  },
];

type RequestProps = {
  Page: number;
  PageSize: number;
  SearchText: string;
  SortBy: string;
  SortOrder: string | number;
};

type StateProps = {
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  results?: ResultProps[];
};

type ResultProps = {
  host: string;
  id: string;
  name: string;
  sequenceNumber: number;
  siteUrl: string;
};

type WebsiteResponseProps = {
  code: string;
  id: string;
  name: string;
};

const WebsitesSuperAdmin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [websites, setWebsites] = useState<RequestProps>({
    Page: page,
    PageSize: limit,
    SearchText: '',
    SortBy: 'siteUrl',
    SortOrder: 0,
  });

  const [state, setState] = useState<ResultProps[]>([]);
  const [addWebsite, setAddWebsite] = useState<{ siteUrl: string; name: string }>({
    siteUrl: '',
    name: '',
  });
  const { siteUrl } = addWebsite;
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [formError, setFormError] = useState<any>({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);

  useEffect(() => {
    const breadcrumb = [{ path: '/websiteAdmin', name: 'Website' }];
    dispatch(update_breadcrumb(breadcrumb));
    getWebsites(websites);
  }, []);

  useEffect(() => {
    if (Object.keys(formError).length === 0 && isSubmit) {
      onAddWebsite();
    }
  }, [formError]);

  const validation = () => {
    const error: any = {};
    if (!siteUrl) {
      error.siteUrl = errorMessage.emptyWebsite;
    }

    return error;
  };

  const getWebsites = async (obj: RequestProps) => {
    dispatch(updateIsLoading(true));
    await instance
      .get('/site', {
        params: obj,
      })
      .then((response) => {
        if (response.status === 200) {
          gotResponse(response.data);
        }
      })
      .catch(() => {
        const obj = {
          isOpen: true,
          message: errorMessage.oops,
          type: flashBarTheme.ERROR,
        };
        dispatch(updateFlashBar(obj));
        dispatch(updateIsLoading(false));
      });
  };

  const gotResponse = (data: StateProps) => {
    setState(data?.results as ResultProps[]);
    setTotalRowCount(data.totalCount);
    setIsSubmit(false);
    dispatch(updateIsLoading(false));
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const format = /^\S*$/;
    format.test(event.target.value) &&
      setAddWebsite({
        ...addWebsite,
        [event.target.name]: event.target.value,
      });

    if (event.target.value !== '') {
      setFormError({ ...formError, [event.target.name]: undefined });
    } else {
      setFormError({
        ...formError,
        [event.target.name]: `${errorMessageAutoCapitalize(
          event.target.name
        )} is required`,
      });
    }
  };

  const onSubmit = () => {
    setFormError(validation());
    setIsSubmit(true);
  };

  const searchHandler = () => {
    getWebsites({
      ...websites,
      SearchText: websites.SearchText,
      PageSize: limit,
    });
  };
  const handleEnterKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      searchHandler();
    }
  };
  const onSearchHanlder = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWebsites({ ...websites, SearchText: e.target.value, Page: 1 });
    setPage(1);
  };

  const sorting = (sortOrder: string) => {
    let temp = '';
    if (sortOrder == SortOrderType.DESC) {
      temp = SortOrderType.ASC;
    } else {
      temp = SortOrderType.DESC;
    }
    setWebsites({ ...websites, SortOrder: temp });
    return temp;
  };

  const sortRecord = (fieldName: string, sortOrder: string) => {
    getWebsites({
      ...websites,
      Page: page,
      PageSize: limit,
      SearchText: websites.SearchText,
      SortBy: fieldName,
      SortOrder: sorting(sortOrder),
    });
    setWebsites({
      ...websites,
      SortBy: fieldName,
      SortOrder: sorting(sortOrder),
    });
  };

  const paginateHandler = {
    onClickPageNumber: (page: number) => {
      getWebsites({ ...websites, Page: page, PageSize: limit });
      setPage(page);
    },
    onNextClick: () => {
      if (page < totalPages(totalRowCount, limit)) {
        getWebsites({
          ...websites,
          Page: page + 1,
          PageSize: limit,
        });
        setPage(page + 1);
      }
    },
    onPrevClick: () => {
      if (page > 1) {
        getWebsites({
          ...websites,
          Page: page - 1,
          PageSize: limit,
        });
        setPage(page - 1);
      }
    },
    onFirstClick: () => {
      if (page !== 1) {
        getWebsites({
          ...websites,
          Page: 1,
          PageSize: limit,
        });
        setPage(1);
      }
    },
    onLastClick: () => {
      if (page !== totalPages(totalRowCount, limit)) {
        getWebsites({
          ...websites,
          Page: totalPages(totalRowCount, limit),
          PageSize: limit,
        });
        setPage(totalPages(totalRowCount, limit));
      }
    },

    limitHandler: (item: number) => {
      getWebsites({
        ...websites,
        Page: 1,
        PageSize: item,
      });
      setLimit(item);
      setPage(1);
    },
  };

  const onAddWebsite = async () => {
    const obj = {
      siteUrl: addWebsite.siteUrl.toLowerCase(),
      name: addWebsite.name,
    };
    dispatch(updateIsLoading(true));
    await instance
      .post('/site', obj)
      .then((response) => {
        if (response.status === 201) {
          setIsFormOpen(false);
          const obj = {
            isOpen: true,
            message: successMessage.SITE_ADD,
            type: flashBarTheme.SUCCESS,
          };
          dispatch(updateFlashBar(obj));
          getWebsites({ ...websites, Page: page, PageSize: limit });
          setAddWebsite({
            ...addWebsite,
            siteUrl: '',
            name: '',
          });
          dispatch(updateIsLoading(false));
        }
      })
      .catch((err) => {
        const obj = {
          isOpen: true,
          message: err.response.data.Message
            ? err.response.data.Message
            : errorMessage.oops,
          type: flashBarTheme.ERROR,
        };
        dispatch(updateFlashBar(obj));
        dispatch(updateIsLoading(false));
      });
  };

  const onClearSearch = () => {
    setWebsites({
      ...websites,
      SearchText: '',
      Page: page,
      PageSize: limit,
    });
    getWebsites({
      ...websites,
      SearchText: '',
      Page: page,
      PageSize: limit,
    });
  };

  const onCancel = () => {
    setIsFormOpen(false);
    setIsSubmit(false);
    setFormError({});
    setAddWebsite({
      ...addWebsite,
      siteUrl: '',
      name: '',
    });
  };

  const viewRecord = (row: WebsiteResponseProps) => {
    navigate(`/websiteDetails/${row.id}`);
  };

  return (
    <div className="px-2 py-1">
      <PageHeading label="Websites Management" />
      <div className="bg-white p-2 rounded-sm shadow-lg">
        <div className="sm:w-full lg:flex items-center justify-between mb-2">
          <SearchInput
            value={websites.SearchText}
            onChange={onSearchHanlder}
            onSearch={searchHandler}
            onClear={() => onClearSearch()}
            handleEnterKeyPress={handleEnterKeyPress}
          />

          <Button
            label="Add"
            type="button"
            buttonType="primary"
            icon={<i className="fa-solid fa-plus"></i>}
            onClick={() => setIsFormOpen(!isFormOpen)}
          />
        </div>
        {isFormOpen && (
          <InputForm
            title={'Add New Website'}
            isFormOpen={isFormOpen}
            onSubmit={onSubmit}
            onCancel={onCancel}
          >
            <div className="lg:flex items-center justify-between">
              <Input
                name={'siteUrl'}
                type={'text'}
                label={'Site Url'}
                value={siteUrl}
                placeholder={`Enter Site Url`}
                onChange={handleChange}
                error={formError?.siteUrl}
              />
            </div>
          </InputForm>
        )}
        <MuiTable
          columns={columns}
          rows={state}
          page={page}
          limit={limit}
          totalRowCount={totalRowCount}
          onSortHandler={sortRecord}
          sortOrder={websites.SortOrder}
          checkBoxShow={false}
          checkBoxValue={'id'}
          onViewHandler={viewRecord}
          paginateHandler={paginateHandler}
          showView={true}
          sortArray={['name', 'host', 'sequenceNumber']}
        />
      </div>
    </div>
  );
};

export default WebsitesSuperAdmin;
